/**
 * مكوّنات عرض بيانات السوق.
 * كل القيم تصل جاهزة من Provider — لا حساب هنا.
 */

import { color } from '@/design/tokens';
import type {
  AlertEntry,
  DealRecord,
  MarketPressure,
  MarketPulseRow,
  Opportunity,
} from '@/domain/types';
import { num, perSqm, sar } from '@/lib/format';
import { Button } from '@/components/primitives/Primitives';
import { Sparkline } from './Charts';

// ------------------------------------------------------------ MetricCard

export function MetricCard({
  label,
  value,
  context,
  tone = 'default',
}: {
  label: string;
  value: string;
  context: string;
  tone?: 'default' | 'up' | 'down' | 'warn';
}): JSX.Element {
  const c =
    tone === 'up' ? color.green : tone === 'down' ? color.red : tone === 'warn' ? color.amber : color.text;
  return (
    <div className="nq-card nq-card--secondary" style={{ flex: '1 1 200px' }}>
      <div className="nq-label">{label}</div>
      <div className="nq-num" dir="ltr" style={{ fontSize: 23, fontWeight: 600, marginTop: 6, color: c }}>
        {value}
      </div>
      <p style={{ marginTop: 8, fontSize: 11.5, color: color.micro, lineHeight: 1.6 }}>{context}</p>
    </div>
  );
}

// -------------------------------------------------- Market Pressure Meter

export function MarketPressureMeter({ pressure }: { pressure: MarketPressure }): JSX.Element {
  return (
    <div data-testid="market-pressure">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11.5,
          color: color.label,
          marginBottom: 8,
        }}
      >
        <span>قوة البائع</span>
        <span>قوة المشتري</span>
      </div>
      <div
        style={{
          position: 'relative',
          height: 10,
          borderRadius: 6,
          background: `linear-gradient(to left, ${color.teal}, #4a5a5c, ${color.red})`,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -4,
            insetInlineStart: `${pressure.buyerPower}%`,
            transform: 'translateX(50%)',
            width: 3,
            height: 18,
            borderRadius: 2,
            background: color.text,
          }}
        />
      </div>
      <div className="nq-num" dir="ltr" style={{ marginTop: 10, fontSize: 19, fontWeight: 600 }}>
        {pressure.buyerPower} / 100
      </div>
      <p style={{ marginTop: 8, fontSize: 13, color: color.textSubtle, lineHeight: 1.7 }}>
        {pressure.statement}
      </p>
    </div>
  );
}

// ------------------------------------------------------------ Deals Table

export function DealsTable({ deals }: { deals: DealRecord[] }): JSX.Element {
  return (
    <div className="nq-table-scroll">
      <table className="nq-table">
        <thead>
          <tr>
            <th>التاريخ</th>
            <th>المساحة</th>
            <th>سعر المتر</th>
            <th>إجمالي الصفقة</th>
            <th>الشارع</th>
          </tr>
        </thead>
        <tbody>
          {deals.map((d) => (
            <tr key={d.id}>
              <td className="nq-num" dir="ltr">{d.date}</td>
              <td className="nq-num" dir="ltr">{num(d.areaSqm)} م²</td>
              <td className="nq-num" dir="ltr">{perSqm(d.pricePerSqm)}</td>
              <td className="nq-num" dir="ltr">{sar(d.totalPrice)}</td>
              <td>{d.street}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------- Pulse List

export function PulseList({ rows }: { rows: MarketPulseRow[] }): JSX.Element {
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      {rows.map((r) => (
        <div
          key={r.label}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '10px 0',
            borderBottom: `1px solid ${color.hairlineSoft}`,
          }}
        >
          <span style={{ fontSize: 13, color: color.textSubtle }}>{r.label}</span>
          <span
            className="nq-num"
            dir="ltr"
            style={{
              fontSize: 14,
              fontWeight: 600,
              color:
                r.tone === 'up'
                  ? color.green
                  : r.tone === 'down'
                    ? color.red
                    : r.tone === 'warn'
                      ? color.amber
                      : color.text,
            }}
          >
            {r.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------ Alert Feed

export function AlertFeed({ alerts }: { alerts: AlertEntry[] }): JSX.Element {
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {alerts.map((a) => (
        <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span
            aria-hidden="true"
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              marginTop: 7,
              flex: '0 0 auto',
              background:
                a.level === 'positive' ? color.green : a.level === 'warning' ? color.amber : color.textSubtle,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 13, color: color.textBody, lineHeight: 1.6 }}>{a.text}</p>
            <span style={{ fontSize: 10.5, color: color.micro }}>{a.at}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------- Opportunity Card

export function OpportunityCard({
  opportunity,
  watched,
  onOpen,
  onWatch,
}: {
  opportunity: Opportunity;
  watched: boolean;
  onOpen: () => void;
  onWatch: () => void;
}): JSX.Element {
  return (
    <div className="nq-card" data-testid="opportunity-card">
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="nq-num" dir="ltr" style={{ fontSize: 12, color: color.label }}>
              #{opportunity.rank}
            </span>
            <span style={{ fontSize: 17, fontWeight: 600 }}>{opportunity.district}</span>
            <span style={{ fontSize: 12, color: color.textSubtle }}>{opportunity.city}</span>
          </div>
          <div
            className="nq-num"
            dir="ltr"
            style={{ marginTop: 8, fontSize: 22, fontWeight: 600, color: color.teal }}
          >
            {opportunity.opportunityScore} / 100
          </div>
          <div style={{ fontSize: 11.5, color: color.micro, marginTop: 2 }}>مؤشر الفرصة</div>
        </div>

        <ul
          style={{
            flex: '1 1 240px',
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'grid',
            gap: 6,
            minWidth: 0,
          }}
        >
          {opportunity.reasons.map((r) => (
            <li key={r} style={{ fontSize: 12.5, color: color.textBody }}>
              · {r}
            </li>
          ))}
        </ul>

        <div style={{ flex: '0 0 auto' }}>
          <Sparkline values={opportunity.spark} />
          <div className="nq-num" dir="ltr" style={{ fontSize: 11.5, color: color.micro, marginTop: 4 }}>
            {perSqm(opportunity.dealPricePerSqm)}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
        <Button onClick={onOpen}>افتح الحي</Button>
        <Button variant="ghost" onClick={onWatch}>
          {watched ? 'تمت المراقبة' : 'راقب'}
        </Button>
      </div>
    </div>
  );
}
